import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import {
  useBlockCustomer,
  useCustomerBlockHistory,
  useUnblockCustomer,
} from '@/services/customer-service';
import { Customer, CustomerBlockHistory } from '@/types/customer-type';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  History,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

const QUICK_BLOCK_REASONS = [
  'Fake order / កម្មង់លេង',
  'Refused delivery / មិនទទួលឥវ៉ាន់',
  'Unreachable phone / តេមិនចូល',
  'Not transfer / មិនវេរលុយ',
  'Spam / Rude behavior',
];

interface BlockCustomerDialogProps {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (updatedCustomer: Customer) => void;
  defaultTab?: 'action' | 'history';
}

export default function BlockCustomerDialog({
  customer,
  open,
  onOpenChange,
  onSuccess,
  defaultTab = 'action',
}: BlockCustomerDialogProps) {
  const [reason, setReason] = useState('');
  const [activeTab, setActiveTab] = useState<'action' | 'history'>(defaultTab);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const blockMutation = useBlockCustomer();
  const unblockMutation = useUnblockCustomer();
  const {
    data: history,
    isLoading: isHistoryLoading,
    refresh: refetchHistory,
  } = useCustomerBlockHistory(open && customer?.id ? String(customer.id) : null);

  const isBlocked = Boolean(customer?.blocked);

  useEffect(() => {
    if (open) {
      setReason('');
      setActiveTab(defaultTab);
    }
  }, [open, customer?.id, defaultTab]);

  if (!customer) return null;

  const handleSubmit = async () => {
    const trimmedReason = reason.trim();
    if (!isBlocked && !trimmedReason) {
      toast({
        title: 'Reason required',
        description: 'Please select or enter a reason for blocking this customer.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      if (isBlocked) {
        const resp = await unblockMutation.executeAsync({
          id: String(customer.id),
          reason: trimmedReason || 'Unblocked by staff',
        });
        if (resp?.data) {
          toast({
            title: 'Customer unblocked',
            description: `${customer.name} can now place orders again.`,
          });
          refetchHistory();
          onSuccess?.(resp.data);
          onOpenChange(false);
        }
      } else {
        const resp = await blockMutation.executeAsync({
          id: String(customer.id),
          reason: trimmedReason,
        });
        if (resp?.data) {
          toast({
            title: 'Customer blocked',
            description: `${customer.name} has been blocked from creating new orders.`,
          });
          refetchHistory();
          onSuccess?.(resp.data);
          onOpenChange(false);
        }
      }
    } catch (err: any) {
      toast({
        title: isBlocked ? 'Failed to unblock customer' : 'Failed to block customer',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderActionBadge = (item: CustomerBlockHistory) => {
    switch (item.action) {
      case 'BLOCK':
        return (
          <Badge variant="destructive" className="gap-1 text-[10px] px-2 py-0.5">
            <Ban className="h-3 w-3" /> Blocked
          </Badge>
        );
      case 'UNBLOCK':
        return (
          <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] px-2 py-0.5">
            <CheckCircle2 className="h-3 w-3" /> Unblocked
          </Badge>
        );
      case 'ORDER_REJECTED':
        return (
          <Badge
            variant="outline"
            className="gap-1 border-amber-400 bg-amber-50 text-amber-800 text-[10px] px-2 py-0.5"
          >
            <AlertTriangle className="h-3 w-3" /> Order Attempt Rejected
          </Badge>
        );
      default:
        return <Badge variant="secondary">{item.action}</Badge>;
    }
  };

  const historyList = Array.isArray(history) ? history : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isBlocked ? (
              <>
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                <span>Manage Blocked Customer</span>
              </>
            ) : (
              <>
                <ShieldAlert className="h-5 w-5 text-red-600" />
                <span>Block Customer</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {customer.name} {customer.phone ? `(${customer.phone})` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Segmented Tab Bar: Action vs Audit History */}
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab('action')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md py-1.5 transition ${
              activeTab === 'action'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {isBlocked ? <ShieldCheck className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
            <span>{isBlocked ? 'Unblock Customer' : 'Block Customer'}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-md py-1.5 transition ${
              activeTab === 'history'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="h-3.5 w-3.5" />
            <span>Block & Attempt History ({historyList.length})</span>
          </button>
        </div>

        {activeTab === 'action' ? (
          <div className="space-y-4 py-2 overflow-y-auto">
            {isBlocked ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-red-700">
                  <Ban className="h-4 w-4 shrink-0" />
                  <span>Currently Blocked</span>
                </div>
                {customer.blockReason && (
                  <p className="break-words">
                    <span className="font-medium">Reason:</span> {customer.blockReason}
                  </p>
                )}
                {(customer.blockedByName || customer.blockedAt) && (
                  <p className="text-red-600">
                    {customer.blockedByName ? `Blocked by ${customer.blockedByName}` : ''}
                    {customer.blockedAt
                      ? ` · ${new Date(customer.blockedAt).toLocaleString()}`
                      : ''}
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                <p className="font-semibold">What happens when you block this customer?</p>
                <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
                  <li>
                    New orders via <strong>Facebook Live comments</strong>,{' '}
                    <strong>Messenger chat</strong>, and <strong>Telegram Mini App (/pos)</strong>{' '}
                    are automatically rejected.
                  </li>
                  <li>
                    Staff receive a real-time alert if this customer attempts to order again.
                  </li>
                </ul>
              </div>
            )}

            {!isBlocked && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">Quick Select Reason</label>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_BLOCK_REASONS.map((chip) => {
                    const selected = reason === chip;
                    return (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => setReason(chip)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition ${
                          selected
                            ? 'border-red-600 bg-red-600 text-white font-medium'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {chip}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="block-reason-input" className="text-xs font-medium text-slate-700">
                {isBlocked ? 'Unblock Note (Optional)' : 'Reason for Blocking *'}
              </label>
              <Textarea
                id="block-reason-input"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  isBlocked
                    ? 'e.g., Customer resolved payment / apologized...'
                    : 'Describe why this customer is being blocked...'
                }
                className="text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="py-2 overflow-y-auto max-h-72 space-y-2.5 pr-1">
            {isHistoryLoading ? (
              <div className="py-8 text-center text-xs text-slate-500">
                Loading block history...
              </div>
            ) : historyList.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500">
                No block or rejected order history recorded for this customer.
              </div>
            ) : (
              historyList.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 text-xs space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    {renderActionBadge(entry)}
                    <span className="flex items-center gap-1 text-[11px] text-slate-500">
                      <Clock className="h-3 w-3" />
                      {entry.actionAt ? new Date(entry.actionAt).toLocaleString() : '-'}
                    </span>
                  </div>
                  {entry.reason && (
                    <p className="text-slate-800 break-words">
                      <span className="font-medium text-slate-600">Note:</span> {entry.reason}
                    </p>
                  )}
                  {entry.actionByName && (
                    <p className="text-[11px] text-slate-500">By {entry.actionByName}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {activeTab === 'action' && (
            <Button
              type="button"
              size="sm"
              variant={isBlocked ? 'default' : 'destructive'}
              className={isBlocked ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
              onClick={handleSubmit}
              disabled={isSubmitting || (!isBlocked && !reason.trim())}
            >
              {isSubmitting
                ? isBlocked
                  ? 'Unblocking...'
                  : 'Blocking...'
                : isBlocked
                  ? 'Confirm Unblock'
                  : 'Block Customer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
